/**
 * Attachment API client.
 *
 * `available` is the only question the interface asks. It is derived server-side from the scan
 * state on every request, so the client never decides availability for itself — a cached "it was
 * clean a moment ago" is exactly the reasoning fail-closed is meant to prevent
 * (docs/engineering/03-lifecycle-and-states.md).
 */

import { request } from '../../api/client';

export type ScanState = 'uploaded' | 'pending_scan' | 'clean' | 'infected' | 'scan_failed';

export interface Attachment {
  id: string;
  display_name: string;
  item_id: string | null;
  byte_size: number;
  content_type: string;
  version_number: number;
  scan_state: ScanState;
  /** The only flag that matters. Never computed on this side. */
  available: boolean;
  unavailable_reason?: string;
  scanned_at: string | null;
  created_at: string;
  uploaded_by: string | null;
}

export function fetchAttachments(liturgyId: string): Promise<{ data: Attachment[] }> {
  return request(`/liturgies/${liturgyId}/attachments`);
}

/**
 * Upload one file as a raw body.
 *
 * Not `FormData`: multipart means the server either buffers the whole request or parses an
 * attacker-controlled framing format, and a single file needs neither. The filename travels as a
 * query parameter and is treated as untrusted text on the far side.
 */
export function uploadAttachment(
  liturgyId: string,
  file: File,
  itemId?: string,
): Promise<Attachment> {
  const params = new URLSearchParams({ filename: file.name });
  if (itemId) params.set('item_id', itemId);

  return request(`/liturgies/${liturgyId}/attachments?${params.toString()}`, {
    method: 'POST',
    body: file,
    headers: { 'content-type': file.type || 'application/octet-stream' },
  });
}

export function removeAttachment(liturgyId: string, attachmentId: string): Promise<void> {
  return request(`/liturgies/${liturgyId}/attachments/${attachmentId}`, { method: 'DELETE' });
}

/**
 * The download address.
 *
 * A plain link rather than a fetch: the browser's own download handling is better than anything
 * reconstructed from a blob, and the server refuses an unavailable file regardless of who asks.
 */
export function downloadUrl(liturgyId: string, attachmentId: string): string {
  return `/api/v1/liturgies/${liturgyId}/attachments/${attachmentId}/content`;
}

/** Whether the state is one a later poll could change. Terminal states are not worth polling. */
export function isPending(state: ScanState): boolean {
  return state === 'uploaded' || state === 'pending_scan';
}
